import { Component, effect, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { AppShellService } from '../../core/services/app-shell.service';
import {
  getExpandedGroupLabels,
  groupMainMenuItems,
  MenuSectionGroup,
} from '../../core/utils/navigation.util';
import { MenuItem } from '../../routes';
import { UiMenuItemComponent } from '../menu-item/ui-menu-item.component';

@Component({
  selector: 'ui-side-nav',
  standalone: true,
  imports: [UiMenuItemComponent],
  host: {
    '[class.ui-side-nav-host--drawer]': 'drawer()',
  },
  template: `
    <nav
      class="ui-side-nav veitros-scrollbar"
      [class.ui-side-nav--drawer]="drawer()"
      [class.ui-side-nav--collapsed]="collapsed()"
      aria-label="Menu principal"
    >
      @for (section of menuSections; track trackSection($index, section)) {
        <section class="ui-side-nav__section">
          @if (!collapsed()) {
            <h2 class="ui-side-nav__section-label">{{ section.label }}</h2>
          }
          <div class="ui-side-nav__section-items">
            @for (item of section.items; track trackItem($index, item)) {
              @if (!item.children) {
                <ui-menu-item
                  [label]="item.label"
                  [icon]="item.icon"
                  [link]="item.link"
                  [collapsed]="collapsed()"
                  (activated)="onItemActivated()"
                />
              } @else {
                <div
                  class="ui-side-nav__group"
                  [class.ui-side-nav__group--active]="groupHasActiveChild(item)"
                  [class.ui-side-nav__group--flyout-open]="collapsed() && flyoutLabel === item.label"
                >
                  <ui-menu-item
                    variant="group"
                    [label]="item.label"
                    [icon]="item.icon"
                    [expanded]="collapsed() ? flyoutLabel === item.label : isExpanded(item.label)"
                    [collapsed]="collapsed()"
                    (activated)="toggleSubmenu(item.label)"
                  />
                  @if (!collapsed() && isExpanded(item.label)) {
                    <div class="ui-side-nav__group-children">
                      @for (child of item.children; track child.label) {
                        @if (shell.canShow(child)) {
                          <ui-menu-item
                            variant="child"
                            [label]="child.label"
                            [link]="child.link"
                            [exactActive]="child.exactActive ?? false"
                            (activated)="onItemActivated()"
                          />
                        }
                      }
                    </div>
                  }
                  @if (collapsed() && flyoutLabel === item.label) {
                    <div class="ui-side-nav__flyout" role="menu">
                      <p class="ui-side-nav__flyout-title">{{ item.label }}</p>
                      @for (child of item.children; track child.label) {
                        @if (shell.canShow(child)) {
                          <ui-menu-item
                            variant="default"
                            [label]="child.label"
                            [icon]="child.icon"
                            [link]="child.link"
                            [exactActive]="child.exactActive ?? false"
                            (activated)="onFlyoutItemActivated()"
                          />
                        }
                      }
                    </div>
                  }
                </div>
              }
            }
          </div>
        </section>
      }
    </nav>
  `,
  styles: [`
    :host {
      display: block;
      min-height: 0;
    }

    :host(:not(.ui-side-nav-host--drawer)) {
      flex: 1 1 auto;
      overflow: hidden;
    }

    .ui-side-nav {
      height: 100%;
      min-height: 0;
      padding: 0.75rem 0.5rem 1rem 0.625rem;
      overflow-y: auto;
      overflow-x: hidden;
      overscroll-behavior: contain;
      --scrollbar-size: 0.375rem;
    }

    .ui-side-nav--drawer {
      padding-top: 0;
      max-height: min(70vh, calc(100dvh - 12rem));
    }

    .ui-side-nav--collapsed {
      padding-inline: 0.375rem;
    }

    .ui-side-nav__section + .ui-side-nav__section {
      margin-top: 1.125rem;
    }

    .ui-side-nav--collapsed .ui-side-nav__section + .ui-side-nav__section {
      margin-top: 0.75rem;
    }

    .ui-side-nav__section-label {
      margin: 0 0 0.375rem;
      padding: 0 0.5rem;
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      color: var(--text-muted, #94a3b8);
    }

    .ui-side-nav__section-items {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }

    .ui-side-nav__group {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      position: relative;
    }

    .ui-side-nav__group--active > .ui-menu-item--group {
      color: var(--brand-primary, #1677ff);
    }

    .ui-side-nav__group--active > .ui-menu-item--group .ui-menu-item__icon {
      background: color-mix(in srgb, var(--brand-primary) 10%, var(--surface-bg, #fff));
      color: var(--brand-primary, #1677ff);
    }

    .ui-side-nav__group-children {
      display: flex;
      flex-direction: column;
      gap: 0.0625rem;
      margin-left: 0.25rem;
      padding-left: 0.25rem;
      border-left: 1px solid color-mix(in srgb, var(--border-color) 80%, transparent);
    }

    .ui-side-nav__flyout {
      position: absolute;
      left: calc(100% + 0.5rem);
      top: 0;
      z-index: 50;
      min-width: 12.5rem;
      padding: 0.375rem;
      border-radius: 0.625rem;
      border: 1px solid var(--border-color);
      background: var(--surface-bg, #fff);
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
    }

    .ui-side-nav__flyout-title {
      margin: 0 0 0.375rem;
      padding: 0.375rem 0.625rem 0.25rem;
      font-size: 0.6875rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--text-muted, #94a3b8);
    }
  `],
})
export class UiSideNavComponent {
  shell = inject(AppShellService);
  private router = inject(Router);

  drawer = input(false);
  collapsed = input(false);
  currentUrl = input('');

  itemActivated = output<void>();

  expandedMenus = new Set<string>();
  flyoutLabel: string | null = null;

  get menuSections(): MenuSectionGroup[] {
    return groupMainMenuItems(this.shell.routes, (item) => this.shell.canShow(item));
  }

  constructor() {
    effect(() => {
      this.syncExpandedMenus(this.currentUrl());
    });

    effect(() => {
      if (this.collapsed()) {
        this.expandedMenus.clear();
      } else {
        this.flyoutLabel = null;
      }
    });
  }

  toggleSubmenu(label: string): void {
    if (this.collapsed()) {
      this.flyoutLabel = this.flyoutLabel === label ? null : label;
      return;
    }

    if (this.expandedMenus.has(label)) {
      this.expandedMenus.delete(label);
    } else {
      this.expandedMenus.add(label);
    }
  }

  isExpanded(label: string): boolean {
    return this.expandedMenus.has(label);
  }

  groupHasActiveChild(item: MenuItem): boolean {
    if (!item.children?.length) return false;
    return item.children.some((child) => {
      if (!child.link || !this.shell.canShow(child)) return false;
      return this.router.isActive(child.link, {
        paths: child.exactActive ? 'exact' : 'subset',
        queryParams: 'ignored',
        fragment: 'ignored',
        matrixParams: 'ignored',
      });
    });
  }

  trackSection(_: number, section: MenuSectionGroup): string {
    return section.id;
  }

  trackItem(_: number, item: MenuItem): string {
    return item.label;
  }

  onItemActivated(): void {
    this.flyoutLabel = null;
    this.itemActivated.emit();
  }

  onFlyoutItemActivated(): void {
    this.flyoutLabel = null;
    this.itemActivated.emit();
  }

  private syncExpandedMenus(url: string): void {
    if (this.collapsed()) return;
    const mainItems = this.shell.routes.filter((item) => !item.menuGroup || item.menuGroup === 'main');
    const activeGroups = getExpandedGroupLabels(url, mainItems);
    activeGroups.forEach((label) => this.expandedMenus.add(label));
  }
}
